import { ROLES as R } from "../core/roles";
import { lud, rule } from "../core/rule";
import { SCOPES, checkScope, normalizeIdList, normalizeId } from "../core/scope";

export const ludDashboardPermissions = {
  view: "ludDashboard.view",
  nav: "ludDashboard.nav",
  // "Back" button on the cashier page: lud() roles only
  backToDashboard: "ludDashboard.backToDashboard",
  // Applications page: submit/manage requests for a store, and decide on them.
  applications: {
    manage: "ludDashboard.applications.manage",
    review: "ludDashboard.applications.review",
  },
  // Direct execution of dashboard actions (backend: POST /actions/execute). LUD
  // administrators only - the accountant (fa) has read access and notes, not this.
  actions: { execute: "ludDashboard.actions.execute" },
  // Working-hours / maintenance requests (branch workflow): who reviews, approves, sends and finalizes them from the Requests list.
  // The server decides every call; administrators of the workflow are `applications.review`.
  branchWorkflow: { review: "ludDashboard.branchWorkflow.review" },
  // Showing a restaurant's stored application passwords on the cashier dashboard (backend: POST /lud-dashboard/credentials/:storeId/reveal).
  // The account must also be approved (account state, checked next to this permission).
  credentials: { reveal: "ludDashboard.credentials.reveal" },
  // Operational writes on the dashboard (raise / cancel a request, attach a request file). Split from `view` so a `new` client can read
  // the same dashboard as a store without being able to raise operational requests (backend: POST /requests, PATCH /requests/:id/cancel,
  // POST /requests/:id/files/:kind).
  requests: { write: "ludDashboard.requests.write" },
  // Editing the store profile through the dashboard Completion endpoint (backend: PATCH /completion/:storeId/:itemId). A `new` client
  // edits the same information through the onboarding draft API (`onboarding.draft.edit`), which also honours the review lock.
  completion: { edit: "ludDashboard.completion.edit" },
  mode: {
    admin: "ludDashboard.mode.admin",
    accountant: "ludDashboard.mode.accountant",
    cashier: "ludDashboard.mode.cashier",
    customer: "ludDashboard.mode.customer",
  },
};

export const ludDashboardGrants = {
  // NA-039 (approved): `fa`, `branch` and `dm` open the dashboard in the mode their
  // grants below select (accountant / cashier / admin). A `branch` user also needs
  // a valid assigned branch (NA-035). Opening the page adds no action permission.
  // `new` clients (a prospective client with a draft store) read the same dashboard as a store; what they may DO is limited to the
  // permissions they hold (never the operational ones, see `new` below) and to their own stores (scope = user.between).
  "ludDashboard.view": rule(lud(R.DMM, R.AM, R.DM, R.FA, R.BRANCH, R.STORE_L, R.STORE, R.NEW), {
    requiresBranch: true,
  }),
  // The sidebar entry: everyone who opens the dashboard except fa, whose workspace is in Billing (the route redirects it there).
  "ludDashboard.nav": rule(lud(R.DMM, R.AM, R.DM, R.BRANCH, R.STORE_L, R.STORE, R.NEW), {
    requiresBranch: true,
  }),
  // Exactly the roles that held `ludDashboard.view` before it was opened to `new`.
  "ludDashboard.requests.write": rule(lud(R.DMM, R.AM, R.DM, R.FA, R.BRANCH, R.STORE_L, R.STORE), {
    requiresBranch: true,
  }),
  "ludDashboard.completion.edit": rule(lud(R.DMM, R.AM, R.DM, R.FA, R.BRANCH, R.STORE_L, R.STORE), {
    requiresBranch: true,
  }),
  "ludDashboard.backToDashboard": rule(lud()),
  // Applications (backend: requireApplicationManager / requireDashboardAdmin).
  // manage: store owners and the administrative roles, only for stores they reach.
  // review: approve, reject, complete, deactivate, reactivate: administrators only.
  "ludDashboard.applications.manage": rule(lud(R.DMM, R.AM, R.DM, R.STORE_L, R.STORE), { scope: SCOPES.RESTAURANT }),
  "ludDashboard.applications.review": rule(lud(R.DMM, R.AM, R.DM)),
  "ludDashboard.actions.execute": rule(lud(R.DMM, R.AM, R.DM)),
  "ludDashboard.branchWorkflow.review": rule(lud(R.AM, R.DMM, R.STORE_L, R.STORE)),
  "ludDashboard.credentials.reveal": rule([R.BRANCH]),
  // which dashboard the user starts in (and may return to)
  "ludDashboard.mode.admin": rule(lud(R.DMM, R.AM, R.DM)),
  "ludDashboard.mode.accountant": rule([R.FA]),
  "ludDashboard.mode.cashier": rule([R.BRANCH], { requiresBranch: true }),
  "ludDashboard.mode.customer": rule([R.STORE, R.STORE_L, R.NEW]),
};

// Mode resolution (kept as is, NA-017): admin -> accountant -> cashier -> customer.
// "cashier" is a dashboard mode held by the `branch` role; the `cashier` user role
// no longer exists (NA-016). Anyone without a mode above gets the
// least-privileged one: customer. A `new` client is a customer too: one dashboard, fewer permissions.
// Modes are view modes, not action permissions.
export const resolveDashboardMode = (can) => {
  if (can(ludDashboardPermissions.mode.admin)) return "admin";
  if (can(ludDashboardPermissions.mode.accountant)) return "accountant";
  if (can(ludDashboardPermissions.mode.cashier)) return "cashier";
  return "customer";
};

// Dashboard data scope (NA-039). The dashboard endpoints return the store list
// the client asks for, so the frontend narrows it to what the user may reach:
//  - ceo-level roles: everything;
//  - `between` set: only those restaurant ids;
//  - `between` empty: staff keep the list (the backend filters), clients and
//    branch users get nothing (never "all restaurants").
export const filterDashboardStores = (stores, { role, between }) => {
  const identity = { role, user: { between: normalizeIdList(between) } };
  return (Array.isArray(stores) ? stores : []).filter((store) => {
    const restaurantId = normalizeId(store?.id);
    // a store without an id cannot be verified: never listed
    return Boolean(restaurantId) && checkScope(SCOPES.RESTAURANT, identity, { restaurantId }).ok;
  });
};

// A `branch` user sees only the branches assigned to them. `assignedBranch`
// entries are a branch `_id` or its name (the backend accepts both, see
// menuAccess), so either identity of the dashboard branch may match.
// Other roles keep the full branch list; with no valid assignment a branch user
// gets none.
export const filterDashboardBranches = (branches, { role, assignedBranch }) => {
  const list = Array.isArray(branches) ? branches : [];
  if (role !== R.BRANCH) return list;
  const assigned = new Set(normalizeIdList(assignedBranch));
  if (assigned.size === 0) return [];
  return list.filter((branch) =>
    [branch?.raw?._id, branch?.raw?.name].map(normalizeId).some((id) => id && assigned.has(id)),
  );
};
